import { useEffect } from 'react';
import { useActiveFixesStore } from './useActiveFixesStore';

// Global map of active SSE connections
const activeConnections = new Map<string, EventSource>();

export function useFixConnection(
  filePath: string,
  prompt: string,
  autoStart: boolean
) {
  const store = useActiveFixesStore();

  useEffect(() => {
    if (!autoStart) return;

    // Check if already running
    if (activeConnections.has(filePath)) {
      console.log('[useFixConnection] Connection already exists for', filePath);
      return;
    }

    // Also check store - don't start if already running
    const existingFix = store.getFix(filePath);
    if (existingFix && existingFix.status === 'running') {
      console.log('[useFixConnection] Fix already running in store for', filePath);
      return;
    }

    console.log('[useFixConnection] Starting fix for', filePath);

    // Start new SSE connection
    const es = new EventSource(
      `http://localhost:9999/api/fix-with-claude?prompt=${encodeURIComponent(prompt)}`
    );
    activeConnections.set(filePath, es);

    es.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      console.log('[useFixConnection] SSE message:', msg.type, 'for', filePath);

      if (msg.type === 'started') {
        store.startFix(filePath, msg.processId);
      } else if (msg.type === 'output') {
        store.appendOutput(filePath, msg.data);
      } else if (msg.type === 'complete') {
        store.setStatus(filePath, 'success');

        // Fetch git diff
        fetch(`http://localhost:9999/api/git-diff/${encodeURIComponent(filePath)}`)
          .then(res => res.json())
          .then(data => store.setGitDiff(filePath, data.diff))
          .catch(err => console.error('Failed to fetch git diff:', err));

        es.close();
        activeConnections.delete(filePath);
      } else if (msg.type === 'error') {
        store.appendOutput(filePath, `\n\nError: ${msg.error}`);
        store.setStatus(filePath, 'error');
        es.close();
        activeConnections.delete(filePath);
      }
    };

    es.onerror = (error) => {
      console.error('[useFixConnection] SSE error for', filePath, error);
      es.close();
      activeConnections.delete(filePath);

      // If we have output, consider it success (process completed)
      const fix = store.getFix(filePath);
      if (fix && fix.output.length > 0 && fix.status === 'running') {
        store.setStatus(filePath, 'success');
      }
    };

    return () => {
      // Don't close connection on unmount - keep running in background
      console.log('[useFixConnection] Component unmounting, but connection stays open for', filePath);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, autoStart]); // Exclude prompt and store to avoid infinite loops

  return {
    isConnected: activeConnections.has(filePath)
  };
}

// Helper to cancel a fix
export function cancelFix(filePath: string, processId: string) {
  const es = activeConnections.get(filePath);
  if (es) {
    es.close();
    activeConnections.delete(filePath);
  }

  // Kill the process
  fetch(`http://localhost:9999/api/cancel-fix/${processId}`, {
    method: 'POST'
  }).catch(err => console.error('Failed to cancel process:', err));
}
